/*Copyright © SITA INC Ireland, SITA INC USA, SITA Switzerland Sarl 2025. Confidential. All rights reserved.*/
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthenticationService } from '../../core/services/authentication.service';
import { SessionmanagementService } from '../../core/services/sessionmanagement.service';
import { LoadingService } from '../../core/services/loading.service';
import { Router } from '@angular/router';
import { Constants } from '../../core/constants';
import { Utils } from '../utils';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { DialogComponent } from '../../layout/common/components/dialog/dialog.component';
import { Subject, merge } from 'rxjs';
import { auditTime, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'sita-password-change',
  standalone: false,
  templateUrl: './password-change.component.html',
  styleUrl: './password-change.component.scss',
})
export class PasswordChangeComponent implements OnInit, OnDestroy {
  passwordMatch = true;
  httpErrorMsg = '';
  showHttpError = false;
  loggedIn = false;
  constants = Constants;
  headerClass = 'admin-color';
  showPassword = false;
  showCurrentPassword = false;

  // Alert properties
  showHttpAlert = false;
  showMismatchAlert = false;
  showSimilarPasswordAlert = false;

  private readonly destroy$ = new Subject<void>();

  changePasswordForm = new FormGroup(
    {
      currentPassword: new FormControl(
        '',
        {
          validators: [Validators.required, Validators.minLength(6)],
          updateOn: 'change',
        }
      ),
      newPassword: new FormControl(
        '',
        {
          validators: [
            Validators.required,
            Validators.pattern(Utils.StrongPasswordRegx),
            Validators.minLength(8),
          ],
          updateOn: 'change',
        }
      ),
      confirmPassword: new FormControl(
        '',
        {
          validators: [
            Validators.required,
            Validators.minLength(8),
            Utils.confirmPasswordMatchValidator(),
          ],
          updateOn: 'change',
        }
      ),
    },
    {
      validators: [Utils.passwordMatchValidator(), Utils.passwordSimilar()],
      updateOn: 'change', // group validators can run normally; controls above govern most churn
    }
  );

  constructor(
    public readonly dialog: MatDialog,
    private readonly authSrv: AuthenticationService,
    private readonly sessionSrv: SessionmanagementService,
    private readonly loader: LoadingService,
    private readonly router: Router
  ) { }

  ngOnInit(): void {
    this.loggedIn = this.sessionSrv.isLoggedIn();

    /**
     * Track form validation errors for alerts.
     * Use auditTime(0) to collapse value/status bursts into a single UI update
     * within the same JS turn.
     */
    merge(this.changePasswordForm.valueChanges, this.changePasswordForm.statusChanges)
      .pipe(auditTime(0), takeUntil(this.destroy$))
      .subscribe(() => this.updateAlertVisibility());

    /**
     * When newPassword changes, ensure confirmPassword gets revalidated
     * (so mismatch appears correctly) WITHOUT creating event cascades.
     */
    this.newPasswordFormField?.valueChanges
      .pipe(auditTime(0), takeUntil(this.destroy$))
      .subscribe(() => {
        // Only revalidate confirmPassword silently
        this.confirmPasswordFormField?.updateValueAndValidity({
          onlySelf: true,
          emitEvent: false,
        });

        // Also re-evaluate group validators silently (mismatch/similar)
        this.changePasswordForm.updateValueAndValidity({
          onlySelf: true,
          emitEvent: false,
        });
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateAlertVisibility(): void {
    // Update mismatch alert visibility
    if (
      this.newPasswordFormField?.dirty &&
      this.confirmPasswordFormField?.dirty &&
      this.changePasswordForm.errors?.['mismatch']
    ) {
      this.showMismatchAlert = true;
    } else {
      this.showMismatchAlert = false;
    }

    // Update similar password alert visibility
    if (
      this.newPasswordFormField?.dirty &&
      this.currentPasswordFormField?.dirty &&
      this.changePasswordForm.errors?.['similarPswd']
    ) {
      this.showSimilarPasswordAlert = true;
    } else {
      this.showSimilarPasswordAlert = false;
    }
  }

  get newPasswordFormField() {
    return this.changePasswordForm.get('newPassword');
  }

  get confirmPasswordFormField() {
    return this.changePasswordForm.get('confirmPassword');
  }

  get currentPasswordFormField() {
    return this.changePasswordForm.get('currentPassword');
  }

  changePasswordSubmit(): void {
    this.loader.showSpinner();
    this.showHttpError = false;
    this.showHttpAlert = false;
    this.httpErrorMsg = '';

    this.changePasswordForm.markAllAsTouched();
    this.changePasswordForm.updateValueAndValidity({ emitEvent: false });

    if (this.changePasswordForm.valid) {
      this.authSrv
        .changePassword(
          this.currentPasswordFormField?.value,
          this.newPasswordFormField?.value
        )
        .subscribe({
          next: () => {
            this.openConfirmationDialog(Constants.PASS_CHANGE_SUCCESS_TITLE, '', false);
          },
          error: (err) => {
            this.loader.hideSpinner();
            this.showHttpError = true;
            this.showHttpAlert = true;
            this.httpErrorMsg = err?.error?.message ?? 'Unable to change password';

            if (
              err instanceof HttpErrorResponse &&
              err.status === 401 &&
              !this.sessionSrv.isLoggedIn()
            ) {
              this.router.navigate(['']);
            }
          },
          complete: () => {
            this.loader.hideSpinner();
          },
        });
    } else {
      this.loader.hideSpinner();
    }
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  closeHttpAlert(): void {
    this.showHttpAlert = false;
    this.showHttpError = false;
    this.httpErrorMsg = '';
  }

  closeMismatchAlert(): void {
    this.showMismatchAlert = false;
  }

  closeSimilarPasswordAlert(): void {
    this.showSimilarPasswordAlert = false;
  }

  private openConfirmationDialog(
    title: string,
    displayText: string,
    isPromptOnly: boolean,
    isError: boolean = false
  ): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.height = 'fit-content';
    dialogConfig.width = '400px';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      title: title,
      bodyText: displayText,
      actionBtnText: 'Login',
      isPrompt: isPromptOnly,
      imgsrc: 'assets/icons/success.svg',
      headerClass: this.headerClass,
      showCheckBox: true,
    };

    const dialogRef = this.dialog.open(DialogComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(() => {
      if (!isError) {
        this.sessionSrv.logout();
      }
    });
  }
}